import React, { useState, useRef, useEffect } from "react";
import { Box, FormHelperText } from "@mui/material";
import UITextField from "@/components/ui-controls/textfield";

const SocialSecurityNumber = (props) => {
  const {
    name,
    value,
    onChange,
    onBlur,
    error,
    disabled,
    title,
    disableFields,
    size = "small",
    required = false,
  } = props;

  // State for each part of the SSN
  const [part1, setPart1] = useState("");
  const [part2, setPart2] = useState("");
  const [part3, setPart3] = useState("");

  const isInitialRender = useRef(true);

  useEffect(() => {
    if (!isInitialRender.current) {
      return;
    }

    // Check if value is defined
    if (value?.length) {
      // Skip initial render
      // Split the value and set the state
      const parts = value.split("-");
      setPart1(parts[0] || "");
      setPart2(parts[1] || "");
      setPart3(parts[2] || "");
      isInitialRender.current = false;
    }
  }, [value]);

  // Create refs for the second and third input fields
  const part1Ref = useRef(null);
  const part2Ref = useRef(null);
  const part3Ref = useRef(null);

  const handleFieldChange = (fieldName, fieldValue) => {
    // Update the state based on the field name
    switch (fieldName) {
      case "part1":
        if (fieldValue.length === 3) {
          part2Ref?.current?.focus();
        }
        if (fieldValue.length <= 3) setPart1(fieldValue);
        break;
      case "part2":
        if (fieldValue.length === 2) {
          part3Ref?.current?.focus();
        } else if (fieldValue.length === 0) {
          part1Ref?.current?.focus();
        }
        if (fieldValue.length <= 2) setPart2(fieldValue);
        break;
      case "part3":
        if (fieldValue.length > 4) {
          return;
        } else if (fieldValue.length === 0) {
          part2Ref?.current?.focus();
        }
        if (fieldValue.length <= 4) setPart3(fieldValue);
        break;
      default:
        break;
    }
  };

  const handleBackspace = (fieldName) => {
    // Move focus to the previous field when backspace is pressed
    switch (fieldName) {
      case "part3":
        if (part3.length === 0) {
          part2Ref?.current?.focus();
        }
        break;
      case "part2":
        if (part2.length === 0) {
          part1Ref?.current?.focus();
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    // Combine parts into the formatted SSN
    if (onChange) {
      if (part1 || part2 || part3) {
        const formattedSSN = `${part1}-${part2}-${part3}`;
        // Invoke the parent onChange callback with the formatted SSN
        onChange({ value: formattedSSN });
      } else {
        onChange({ value: "" });
      }
    }
  }, [part1, part2, part3]);

  const rootRef = useRef(null);

  const handleBlur = () => {
    // Moving between the three segments is not leaving the field.
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active && rootRef.current?.contains(active)) return;
      if (onBlur) onBlur();
    });
  };

  return (
    <Box ref={rootRef}>
      <Box
        sx={{ display: "flex", alignItems: "baseline", textAlign: "center" }}
      >
        <UITextField
          title={title}
          InputLabelProps={{
            shrink: true,
          }}
          required={required}
          type="number"
          name={`${name}-part1`}
          value={part1}
          onChange={(e) => handleFieldChange("part1", e.target.value)}
          onKeyDown={(e) => e.key === "Backspace" && handleBackspace("part1")}
          onBlur={handleBlur}
          error={error}
          disabled={disabled || disableFields}
          maxLength={3}
          inputRef={part1Ref}
          inputPropsStyles={{
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            textAlign: "center",
          }}
          sx={{
            "& .MuiInputBase-input": {
              textAlign: "center",
            },
          }}
          placeholder="###"
          showErrorText={false}
          size={size}
        />
        <UITextField
          type="number"
          name={`${name}-part2`}
          value={part2}
          onChange={(e) => handleFieldChange("part2", e.target.value)}
          onKeyDown={(e) => e.key === "Backspace" && handleBackspace("part2")}
          onBlur={handleBlur}
          error={error}
          disabled={disabled || disableFields}
          maxLength={2}
          inputRef={part2Ref}
          inputPropsStyles={{ borderRadius: 0, textAlign: "center" }}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderLeftWidth: "0px",
              borderRightWidth: "0px",
            },
            "& .MuiInputBase-input": {
              textAlign: "center",
            },
          }}
          placeholder="##"
          showErrorText={false}
          size={size}
        />
        <UITextField
          type="number"
          name={`${name}-part3`}
          value={part3}
          onChange={(e) => handleFieldChange("part3", e.target.value)}
          onKeyDown={(e) => e.key === "Backspace" && handleBackspace("part3")}
          onBlur={handleBlur}
          error={error}
          disabled={disabled || disableFields}
          maxLength={4}
          inputRef={part3Ref}
          sx={{
            "& .MuiInputBase-input": {
              textAlign: "center",
            },
          }}
          inputPropsStyles={{
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          }}
          placeholder="####"
          showErrorText={false}
          size={size}
        />
      </Box>
      {error ? (
        <FormHelperText error sx={{ mx: 0, mt: "4px", lineHeight: 1.2 }}>
          {error}
        </FormHelperText>
      ) : null}
    </Box>
  );
};

export default SocialSecurityNumber;
